import { useSelection } from '#hooks/PRIVATE_useVisSelection';
import type {
  DiagramLinkData,
  DiagramNodeData,
  ItemInfo,
  PromotedLinkId,
  SelectedItemDetail
} from '../diagram.types';
import { getNavigableInfo } from '../utils/diagramUtils';
import type { RefObject } from 'preact';

type SelectionProps<K1, K2, D1, D2> = {
  selectedIds: {
    nodes?: K1[];
    links?: K2[];
    promotedLinks?: PromotedLinkId<K1, D2>[];
  };
  selectionMode: 'none' | 'single' | 'multiple';
  nodeMap: {
    itemIdToDataMap: Map<K1, D1>;
  };
  linkMap: {
    itemIdToDataMap: Map<K2, D2>;
  };
  focusedItemInfo: ItemInfo<K1, K2, D2>;
  onSelectionChange?: (detail: SelectedItemDetail<K1, K2, D1, D2>) => void;
  rootRef: RefObject<HTMLDivElement>;
  promotedLinkMap: Map<string, PromotedLinkId<K1, D2>>;
};

type EncodedNodeId = `N_${string}`;
type EncodedLinkId = `L_${string}`;
type EncodedPromotedLinkId = `P_${string}`;

export function useDiagramSelection<
  K1,
  K2,
  D1 extends DiagramNodeData<K1, D1>,
  D2 extends DiagramLinkData<K2, K1>
>({
  selectedIds,
  selectionMode,
  nodeMap,
  linkMap,
  focusedItemInfo,
  onSelectionChange,
  rootRef,
  promotedLinkMap
}: SelectionProps<K1, K2, D1, D2>) {
  const nodeIdToDataMap = nodeMap.itemIdToDataMap;
  const nodeEncodedToIdMap = new Map<string, K1>();
  const linkEncodedToIdMap = new Map<string, K2>();

  const linkIdToDataMap = linkMap.itemIdToDataMap;
  const encodeNodeId = (id: K1) => {
    const encoded = `N_${id}` as EncodedNodeId;
    nodeEncodedToIdMap.set(encoded, id);
    return encoded;
  };
  const decodeNodeId = (id: EncodedNodeId) => nodeEncodedToIdMap.get(id);
  const encodeLinkId = (id: K2) => {
    const encoded = `L_${id}` as EncodedLinkId;
    linkEncodedToIdMap.set(encoded, id);
    return encoded;
  };
  const decodeLinkId = (id: EncodedLinkId) => linkEncodedToIdMap.get(id);
  const encodePromotedLinkId = (id: PromotedLinkId<K1, D2>) =>
    `P_${JSON.stringify(id)}` as EncodedPromotedLinkId;
  const decodePromotedLinkId = (id: EncodedPromotedLinkId) => JSON.parse(id.slice(2));

  const encodedNodesSelection = selectedIds.nodes ? selectedIds.nodes.map(encodeNodeId) : [];
  const encodedLinksSelection = selectedIds.links ? selectedIds.links.map(encodeLinkId) : [];
  const encodedPromotedLinksSelection = selectedIds.promotedLinks
    ? selectedIds.promotedLinks.map(encodePromotedLinkId)
    : [];
  const encodedSelection = [
    ...encodedNodesSelection,
    ...encodedLinksSelection,
    ...encodedPromotedLinksSelection
  ];
  const idExtracter = (event: Event) => {
    // return encodeNodeId(id) or encodeLinkId(id) depending on node or link selection or undefined if clicked on empty space
    const info =
      event.type === 'keyup'
        ? focusedItemInfo
        : getNavigableInfo<K1, K2, D2>(event.target as HTMLElement, promotedLinkMap, rootRef);
    return info.id !== undefined
      ? info.type === 'node'
        ? encodeNodeId(info.id as K1)
        : info.type === 'link'
        ? encodeLinkId(info.id as K2)
        : encodePromotedLinkId(info.id)
      : undefined;
  };

  const onEncodedSelectionChange = (detail: {
    ids: (EncodedNodeId | EncodedLinkId | EncodedPromotedLinkId)[];
  }) => {
    const newNodeIds = detail.ids
      .filter((encId) => encId[0] === 'N')
      .map((encId) => decodeNodeId(encId as EncodedNodeId)!);
    const newLinkIds = detail.ids
      .filter((encId) => encId[0] === 'L')
      .map((encId) => decodeLinkId(encId as EncodedLinkId)!);

    const newPromotedLinkIds = detail.ids
      .filter((encId) => encId[0] === 'P')
      .map((encId) => decodePromotedLinkId(encId as EncodedPromotedLinkId)!);

    const newSelectedNodeIds = newNodeIds.filter(
      (id) => nodeIdToDataMap.get(id)?.selectable !== 'off'
    );
    const newSelectedLinkIds = newLinkIds.filter(
      (id) => linkIdToDataMap.get(id)?.selectable !== 'off'
    );

    const nodeData = newSelectedNodeIds.map((selectedId) => {
      return nodeIdToDataMap.get(selectedId);
    });
    const linkData = newSelectedLinkIds.map((selectedId) => {
      return linkIdToDataMap.get(selectedId);
    });
    return (
      onSelectionChange &&
      onSelectionChange({
        nodes: {
          ids: newSelectedNodeIds,
          data: nodeData
        },
        links: {
          ids: newSelectedLinkIds,
          data: linkData
        },
        promotedLinks: {
          ids: newPromotedLinkIds
        }
      })
    );
  };

  const getDataById = (id: EncodedNodeId | EncodedLinkId | EncodedPromotedLinkId) => {
    let data;
    if (id[0] === 'N') {
      const nodeId = decodeNodeId(id as EncodedNodeId)!;
      data = nodeMap.itemIdToDataMap.get(nodeId);
    } else if (id[0] === 'L') {
      const linkId = decodeLinkId(id as EncodedLinkId)!;
      data = linkMap.itemIdToDataMap.get(linkId);
    } else if (id[0] === 'P') {
      data = decodePromotedLinkId(id as EncodedPromotedLinkId)!;
    }
    return data;
  };

  const isKeyboardSelection = (event: KeyboardEvent) => {
    return event.key === '[' || event.key === ']';
  };

  const { selectionContent, selectionProps } = useSelection<
    EncodedNodeId | EncodedLinkId | EncodedPromotedLinkId,
    D1 | D2 | DiagramLinkData<string, K1>
  >({
    selection: encodedSelection,
    idExtracter,
    selectionMode,
    getDataById,
    onChange: onEncodedSelectionChange,
    isKeyboardSelection
  });

  return { selectionContent, selectionProps };
}
